import React, { useState, useEffect } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateTimePicker, DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { Grid } from '@ts/storybook';
import ClearIcon from '@mui/icons-material/Clear';
import { withTranslation, WithTranslation } from 'react-i18next';
import { toDayjsLocale } from '@utils/locales/locale.utils';

interface DateTimeInputProps extends WithTranslation {
  onlyDate?: boolean;
  onlyTime?: boolean;
  disabled?: boolean;
  isReadOnly?: boolean;
  label?: string;
  labelDate?: string;
  labelTime?: string;
  dateFormat?: string;
  timeFormat?: string;
  timezone?: string;
  value?: string | Date;
  onChange?: (dateTime: string | Date | null) => void;
  textFieldStyle?: React.CSSProperties;
  datePickerStyle?: React.CSSProperties;
  timePickerStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  sx?: React.CSSProperties;
}

dayjs.extend(utc);
dayjs.extend(timezone);

type tTypeDate = 'date' | 'time' | 'datetime';

const DateTimeInput: React.FC<DateTimeInputProps> = props => {
  const t = (key: string | undefined) => key && props.t(key).toString();
  const lng = props.i18n.language;

  const dayjsLocale = toDayjsLocale(lng);

  //#region Handler
  const handleOpenClose = (type: tTypeDate) => {
    const datePick = type === 'date' && !picker.date;
    let timePick = type === 'time' && !picker.time;
    if (type === 'date' && picker.date && !state.time) timePick = true;
    setPicker({ ...picker, date: datePick, time: timePick });
  };

  const handleChangeValue = (type: tTypeDate, value: Dayjs) => {
    setState({ ...state, [type]: value });
    if (typeof props.onChange === 'function') {
      props.onChange(createDateTime(type, value));
    }
  };
  //#endregion

  //#region Functions
  const importDayjsLocale = async (locale: string | undefined) => {
    if (locale !== 'en') {
      const dayjsLocale = await import(`dayjs/locale/${locale}`);
      dayjs.locale(dayjsLocale.default);
    }
  };
  const clean = (type: tTypeDate, event: React.MouseEvent) => {
    event.stopPropagation();
    setState({ ...state, [type]: null });
    if (typeof props.onChange === 'function') {
      props.onChange(null);
    }
  };
  const createDateTime = (type: tTypeDate, value: Dayjs) => {
    console.log('value', value);
    const momentDate = dayjs(
      type === 'date' || type === 'datetime' ? value?.toDate() : (state.date && state.date.toDate()) || value?.toDate()
    );
    const momentTime = dayjs(
      type === 'time' || type === 'datetime' ? value?.toDate() : (state.time && state.time.toDate()) || value?.toDate()
    );

    const out = dayjs(
      momentDate.year() +
        '-' +
        (momentDate.month() + 1) +
        '-' +
        momentDate.date() +
        'T' +
        momentTime.hour() +
        ':' +
        momentTime.minute() +
        ':' +
        '0',
      { format: 'YYYY-MM-DDTHH:mm:ssZ' }
    );

    return out.toDate();
  };
  //#endregion

  const {
    onlyDate,
    onlyTime,
    labelDate,
    labelTime,
    disabled,
    isReadOnly,
    timeFormat,
    dateFormat,
    textFieldStyle,
    timePickerStyle,
    datePickerStyle,
  } = props;
  dayjs.locale(dayjsLocale);
  const dateTimeObject = dayjs(props.value, { format: 'YYYY-MM-DDTHH:mm:ss.sssZ' });

  const [locale, setLocale] = useState(dayjsLocale);
  const [state, setState] = useState<{
    date: Dayjs | undefined;
    time: Dayjs | undefined;
  }>({
    date: (props.value && dateTimeObject) || undefined,
    time: (props.value && dateTimeObject) || undefined,
  });
  const [picker, setPicker] = useState<{
    date: boolean;
    time: boolean;
  }>({
    date: false,
    time: false,
  });

  useEffect(() => {
    importDayjsLocale(dayjsLocale);
    setLocale(dayjsLocale);
  }, [dayjsLocale]);

  useEffect(() => {
    setState({
      date: (props.value && dateTimeObject) || undefined,
      time: (props.value && dateTimeObject) || undefined,
    });
  }, [props.value]);

  return (
    <Grid container spacing={2} justifyContent="flex-start">
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        {!onlyTime && !onlyDate && (
          <Grid size={{ xs: 12 }}>
            <DateTimePicker
              //timezone={props.timezone || 'UTC'}
              label={t(props.label)}
              value={state.date || null}
              onChange={v => handleChangeValue('datetime', v as Dayjs)}
              disabled={disabled}
              readOnly={isReadOnly}
              ampm={false}
              slotProps={{
                textField: {
                  variant: 'standard',
                  fullWidth: true,
                  style: textFieldStyle,
                  //onClick: () => handleOpenClose('date'),
                } /* 
              inputAdornment: {
                disablePointerEvents: disabled,
                onClick: (event: React.MouseEvent) => clean('date', event),
                style: { display: state.date ? 'flex' : 'none' },
              }, */,
              }}
              sx={{ ...props.sx, ...(isReadOnly && { pointerEvents: 'none' }) }}
            />
          </Grid>
        )}
        {!onlyTime && onlyDate ? (
          <Grid size={{ xs: onlyDate ? 12 : 6 }}>
            <DatePicker
              //timezone={props.timezone || 'UTC'}
              disabled={disabled}
              //ampm={false}
              label={t(labelDate)}
              //error={false}
              readOnly={isReadOnly}
              open={picker.date}
              //invalidDateMessage=""
              format={dateFormat || 'MM/DD/YYYY'}
              value={state.date || null}
              onChange={v => handleChangeValue('datetime', v as Dayjs)}
              onClose={() => handleOpenClose('date')}
              slots={{
                openPickerIcon: ClearIcon,
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  fullWidth: true,
                  style: textFieldStyle,
                  onClick: () => handleOpenClose('date'),
                },
                inputAdornment: {
                  disablePointerEvents: disabled,
                  onClick: (event: React.MouseEvent) => clean('date', event),
                  style: { display: state.date ? 'flex' : 'none' },
                },
              }}
              sx={datePickerStyle}
            />
          </Grid>
        ) : null}
        {!onlyDate && onlyTime ? (
          <Grid size={{ xs: onlyTime ? 12 : 6 }}>
            <TimePicker
              //timezone={props.timezone || 'UTC'}
              label={t(labelTime)}
              format={timeFormat || 'HH:mm'}
              open={picker.time}
              ampm={false}
              disabled={disabled}
              readOnly={isReadOnly}
              //invalidTimeMessage=""
              //error={false}
              value={state.time || null}
              onChange={v => handleChangeValue('time', v as Dayjs)}
              onClose={() => handleOpenClose('time')}
              slots={{
                openPickerIcon: ClearIcon,
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  fullWidth: true,
                  style: textFieldStyle,
                  onClick: () => handleOpenClose('time'),
                },
                inputAdornment: {
                  disablePointerEvents: disabled,
                  onClick: (event: React.MouseEvent) => clean('time', event),
                  style: { display: state.time ? 'flex' : 'none' },
                },
              }}
              sx={timePickerStyle}
            />
          </Grid>
        ) : null}
      </LocalizationProvider>
    </Grid>
  );
};

export default withTranslation()(DateTimeInput);
